import type { TokenPreview } from '@src/components/TokenRow/TokenRow';
import type { CssVariable } from '@src/pages/UiKitPage/readCssVariables';

export type { TokenPreview };

export interface TokenGroupDefinition {
  id: string;
  label: string;
  hint: string;
  /** Token name prefixes, in the order they should be listed. */
  prefixes: string[];
  preview: TokenPreview;
}

export interface TokenGroup extends TokenGroupDefinition {
  variables: CssVariable[];
}

/**
 * Only grouping and copy live here — no values. Which tokens exist comes from
 * the stylesheet itself (see readCssVariables), so this list never goes stale;
 * at worst a brand-new prefix lands in the catch-all group below.
 */
export const TOKEN_GROUPS: TokenGroupDefinition[] = [
  {
    id: 'colour',
    label: 'Colour',
    hint: 'Semantic, never literal. Each has a light and a dark value in theme/winter/colors.css.',
    prefixes: ['--color-'],
    preview: 'color',
  },
  {
    id: 'spacing',
    label: 'Spacing',
    hint: 'A 4px scale: the step number is the multiple of 4px. Padding, margin and gaps.',
    prefixes: ['--space-'],
    preview: 'length',
  },
  {
    id: 'radius',
    label: 'Radius',
    hint: 'Buttons and chips are pills; cards use xl and 2xl.',
    prefixes: ['--radius-'],
    preview: 'radius',
  },
  {
    id: 'borders',
    label: 'Border widths',
    hint: 'Emphasis is the 1.5px used where a hairline would not read as deliberate.',
    prefixes: ['--border-width-'],
    preview: 'length',
  },
  {
    id: 'typography',
    label: 'Typography',
    hint: 'Gilroy, six weights. Copy uses a type role via Typography — not these tokens directly.',
    prefixes: [
      '--font-family-',
      '--font-size-',
      '--font-weight-',
      '--line-height-',
      '--letter-spacing-',
    ],
    preview: 'value',
  },
  {
    id: 'sizes',
    label: 'Fixed sizes',
    hint: 'Square controls, icons and avatars. Pill buttons take height from padding instead.',
    prefixes: ['--size-'],
    preview: 'length',
  },
  {
    id: 'elevation',
    label: 'Elevation',
    hint: 'Composed from the themed shadow colour, so shadows go pure black on dark.',
    prefixes: ['--shadow-'],
    preview: 'shadow',
  },
  {
    id: 'layout',
    label: 'Layout',
    hint: 'Page width and gutters.',
    prefixes: ['--layout-'],
    preview: 'length',
  },
  {
    id: 'motion',
    label: 'Motion',
    hint: 'One easing curve, three speeds. Reduced-motion collapses all of them.',
    prefixes: ['--duration-', '--easing-'],
    preview: 'value',
  },
  {
    id: 'interaction',
    label: 'Interaction',
    hint: 'Focus ring geometry and the hover lift shared by raised surfaces.',
    prefixes: ['--focus-ring-', '--translate-', '--z-index-', '--blur-'],
    preview: 'value',
  },
];

const CATCH_ALL: TokenGroupDefinition = {
  id: 'other',
  label: 'Ungrouped',
  hint: 'Tokens with a prefix that tokenGroups.ts does not know about yet.',
  prefixes: [],
  preview: 'value',
};

/**
 * Buckets tokens by prefix. Anything unrecognised is surfaced in `Ungrouped`
 * rather than dropped, so a new prefix is visible instead of invisible.
 */
export function groupTokens(variables: CssVariable[]): TokenGroup[] {
  const claimed = new Set<string>();
  const groups: TokenGroup[] = [];

  for (const definition of TOKEN_GROUPS) {
    const matched: CssVariable[] = [];
    // Prefix order, not declaration order: it keeps --font-size-* together
    // rather than interleaved with --font-weight-*.
    for (const prefix of definition.prefixes) {
      for (const variable of variables) {
        if (variable.name.startsWith(prefix) && !claimed.has(variable.name)) {
          claimed.add(variable.name);
          matched.push(variable);
        }
      }
    }
    if (matched.length > 0) {
      groups.push({ ...definition, variables: matched });
    }
  }

  const unclaimed = variables.filter((variable) => !claimed.has(variable.name));
  if (unclaimed.length > 0) {
    groups.push({ ...CATCH_ALL, variables: unclaimed });
  }

  return groups;
}
